import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
	Box,
	Button,
	HStack,
	Stack,
	Text,
	Tooltip,
} from "@chakra-ui/react";
import { COLUMN_TYPE_DESCRIPTIONS, COLUMN_TYPE_OPTIONS } from "./column-types";
import type { ColumnConfig, QuoteColumnType } from "./column-types";
import {
	CompactSelectWithLabel,
	CompactTextInput,
	type CompactSelectOption,
} from "newui/components/ui/InputWithLabel";

// Single-line guideline — InfoTooltip renders text as a plain child so
// embedded newlines collapse. Hover an individual option for per-type detail
// (wired via the `title` attribute on each <option>).
const COLUMN_TYPE_GUIDELINE =
	"Pick how suppliers fill this column — Description/Units are procurer-defined; Response: Quantity, Value, Saving (discount), Number and Text are supplier inputs; Total auto-sums Value and Saving columns. Hover an option for details.";

export interface ColumnEditPopoverProps {
	column: ColumnConfig;
	isDeleteDisabled: boolean;
	isOpen: boolean;
	triggerRef: React.RefObject<HTMLElement | null>;
	getDisabledReason: (type: QuoteColumnType) => string;
	isTypeDisabled: (type: QuoteColumnType, currentType: QuoteColumnType) => boolean;
	/** Optional filter — types where this returns true are omitted entirely. */
	isTypeHidden?: (type: QuoteColumnType, currentType: QuoteColumnType) => boolean;
	onClose: () => void;
	onDelete: () => void;
	onUpdateLabel: (label: string) => void;
	onUpdateType: (type: QuoteColumnType) => void;
}

const POPOVER_WIDTH = 320;
const POPOVER_ESTIMATED_HEIGHT = 260;
const GAP = 6;

/**
 * Inline editor for a column header — text label + type dropdown + delete.
 *
 * Renders as a portal-positioned panel anchored to `triggerRef` via
 * `getBoundingClientRect()`. Chakra's `<Popover>` was not used because, when
 * the popover lives outside the table for DOM-nesting reasons, Chakra cannot
 * find the trigger and falls back to top-left of the viewport — same approach
 * as `ColumnContextMenu` for consistency.
 *
 * Escape calls `event.stopPropagation()` so the parent Modal does not also
 * close. Click-outside dismiss is wired via a document mousedown listener.
 */
export const ColumnEditPopover = ({
	column,
	isOpen,
	onClose,
	onUpdateLabel,
	onUpdateType,
	onDelete,
	isDeleteDisabled,
	isTypeDisabled,
	isTypeHidden,
	getDisabledReason,
	triggerRef,
}: ColumnEditPopoverProps) => {
	const labelInputRef = useRef<HTMLInputElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const [labelDraft, setLabelDraft] = useState(column.label);

	// Keep local draft in sync if the column changes (e.g. type swap mid-edit).
	useEffect(() => {
		setLabelDraft(column.label);
	}, [column.localId, column.label]);

	// Focus the input on open; return focus to trigger on close.
	useEffect(() => {
		if (isOpen) {
			const t = window.setTimeout(() => labelInputRef.current?.focus(), 0);
			return () => window.clearTimeout(t);
		}
		return undefined;
	}, [isOpen]);

	// Click-outside dismiss. Ignore clicks on the trigger so it can toggle.
	useEffect(() => {
		if (!isOpen) return undefined;
		const handler = (event: MouseEvent) => {
			const target = event.target as Node;
			if (panelRef.current?.contains(target)) return;
			if (triggerRef.current?.contains(target)) return;
			onClose();
		};
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, [isOpen, onClose, triggerRef]);

	const handleClose = () => {
		onClose();
		setTimeout(() => triggerRef.current?.focus(), 0);
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === "Escape") {
			event.stopPropagation();
			handleClose();
		}
	};

	if (!isOpen) return null;
	if (typeof document === "undefined") return null;

	// Anchor under the trigger; flip if it would overflow the viewport.
	const rect = triggerRef.current?.getBoundingClientRect();
	const viewportWidth =
		typeof window !== "undefined" ? window.innerWidth : 1024;
	const viewportHeight =
		typeof window !== "undefined" ? window.innerHeight : 768;
	const anchorLeft = rect?.left ?? 0;
	const anchorBottom = rect?.bottom ?? 0;
	const anchorTop = rect?.top ?? 0;
	const left =
		anchorLeft + POPOVER_WIDTH > viewportWidth
			? Math.max(8, viewportWidth - POPOVER_WIDTH - 8)
			: anchorLeft;
	const top =
		anchorBottom + POPOVER_ESTIMATED_HEIGHT > viewportHeight
			? Math.max(8, anchorTop - POPOVER_ESTIMATED_HEIGHT - GAP)
			: anchorBottom + GAP;

	const panel = (
		<Box
			ref={panelRef}
			role="dialog"
			aria-label="Edit column"
			data-testid="column-edit-popover"
			position="fixed"
			left={`${left}px`}
			top={`${top}px`}
			zIndex={9999}
			bg="white"
			boxShadow="lg"
			borderRadius="md"
			border="1px solid"
			borderColor="gray.200"
			minW={`${POPOVER_WIDTH}px`}
			p={3}
			onKeyDown={handleKeyDown}
		>
			<Stack spacing={3}>
				<Stack spacing={1}>
					<Text fontSize="sm" fontWeight="medium">
						Header text
					</Text>
					<CompactTextInput ref={labelInputRef}
						aria-label="Column header text"
						value={labelDraft}
						onChange={(e) => {
							setLabelDraft(e.target.value);
							onUpdateLabel(e.target.value);
						}} />
				</Stack>

				<CompactSelectWithLabel
					aria-label="Column type"
					guideLine={COLUMN_TYPE_GUIDELINE}
					label="Column type"
					name={`column-type-${column.localId}`}
					options={COLUMN_TYPE_OPTIONS
						.filter((opt) => !isTypeHidden?.(opt.value, column.columnType))
						.map<CompactSelectOption>((opt) => {
						const disabled = isTypeDisabled(opt.value, column.columnType);
						const reason = disabled ? getDisabledReason(opt.value) : "";
						return {
							value: opt.value,
							disabled,
							// Show the disabled reason inline so the user understands
							// why an option is not available without hunting tooltips.
							label: `${opt.label}${disabled && reason ? ` — ${reason}` : ""}`,
							// Always surface the per-option description as a hover
							// tooltip (and the disabled reason when applicable).
							title: reason || COLUMN_TYPE_DESCRIPTIONS[opt.value],
						};
					})}
					value={column.columnType}
					onChange={(e) =>
						onUpdateType(e.target.value as QuoteColumnType)
					}
				/>

				<HStack justify="space-between" pt={1}>
					<Tooltip
						label={isDeleteDisabled ? "Cannot delete the only column" : ""}
						isDisabled={!isDeleteDisabled}
					>
						<span>
							<Button
								size="sm"
								colorScheme="red"
								variant="ghost"
								onClick={onDelete}
								isDisabled={isDeleteDisabled}
								aria-label="Delete column"
							>
								Delete
							</Button>
						</span>
					</Tooltip>
					<Button
						size="sm"
						colorScheme="blue"
						onClick={handleClose}
						aria-label="Done editing column"
					>
						Done
					</Button>
				</HStack>
			</Stack>
		</Box>
	);

	return createPortal(panel, document.body);
};

export default ColumnEditPopover;
